import { useMemo } from 'react'
import { PropertyPageQuery } from 'gql/graphql'
import Headers from 'components/home/common/typography/Headers'
import Paragraphs from 'components/home/common/typography/Paragraphs'

const PropertyAmenitiesSection = ({
  property,
}: {
  property: PropertyPageQuery['property']
}) => {
  const amenities = useMemo(() => (Object
    .entries({
      'general': property?.general_amenities?.filter(amenity => amenity?.parent !== null),
      'outdoor': property?.outdoor_amenities?.filter(amenity => amenity?.parent !== null),
      'entertainment': property?.entertainment_amenities?.filter(amenity => amenity?.parent !== null),
    })
    .filter(([_, amenities]) => (amenities ?? []).length > 0)
  ), [
    property?.general_amenities,
    property?.outdoor_amenities,
    property?.entertainment_amenities,
  ])

  const included = useMemo(() => ([
    ...(property?.linen_towels_services ?? []),
    ...(property?.housekeeping_services ?? []),
    ...(property?.staff_services ?? []),
    ...(property?.other_services ?? []),
  ]), [
    property?.housekeeping_services,
    property?.linen_towels_services,
    property?.staff_services,
    property?.other_services,
  ])

  if (!property) {
    return null
  }

  return (
    <div className="w-full">
      <Headers.XXL className="text-center px-70 text-grey-900">
        Amenities
      </Headers.XXL>

      <div className="hidden lg:flex lg:flex-col mt-55">
        <div className="flex justify-between">
          {amenities.map(([type], counter) => (
            <Paragraphs.PropertyPage
              key={`${type}-${counter}`}
              className="w-full antialiased font-bold text-center uppercase text-grey-800 tracking-4"
            >
              {type}
            </Paragraphs.PropertyPage>
          ))}
          {included.length > 0 && (
            <Paragraphs.PropertyPage className="w-full antialiased font-bold text-center uppercase text-grey-800 tracking-4">
              Included
            </Paragraphs.PropertyPage>
          )}
        </div>

        <div className="flex justify-between divide-x mt-30 divide-grey-250">
          {amenities.map(([type, amenities]) => (
            <ol key={type} className="flex flex-col w-full gap-15">
              {
                amenities
                  ? amenities.map((amenity, key) => (
                    <li key={key}>
                      <Paragraphs.PropertyPage className="w-full antialiased text-center text-grey-500">
                        {amenity.value}
                      </Paragraphs.PropertyPage>
                    </li>
                  ))
                  : null
              }
            </ol>
          ))}
          {included.length > 0 && (
            <ol className="flex flex-col w-full gap-15">
              {included.map((amenity, key) => (
                <li key={key}>
                  <Paragraphs.PropertyPage className="w-full antialiased text-center text-grey-500">
                    {amenity.value}
                  </Paragraphs.PropertyPage>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-center mx-auto lg:hidden mt-42 gap-50">
        {amenities.map(([type, amenities]) => (
          amenities
            ? (
              <div key={type} className="w-300">
                <Paragraphs.PropertyPage className="w-full antialiased font-bold text-center uppercase text-grey-800 tracking-4">
                  {type}
                </Paragraphs.PropertyPage>
                <div className="mt-15">
                  <ul className="flex flex-col w-full gap-8">
                    {amenities.map((amenity, key) => (
                      <li key={`${type}-${key}`}>
                        <Paragraphs.PropertyPage className="w-full antialiased text-center text-grey-500">
                          {amenity.value}
                        </Paragraphs.PropertyPage>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )
            : null
        ))}

        {included.length > 0 && (
          <div className="w-[300px]">
            <Paragraphs.PropertyPage className="w-full antialiased font-bold text-center uppercase text-grey-800 tracking-4">
              Included
            </Paragraphs.PropertyPage>
            <div className="mt-15">
              <ul className="flex flex-col w-full gap-8">
                {included.map((amenity, key) => (
                  <li key={key}>
                    <Paragraphs.PropertyPage className="w-full antialiased text-center text-grey-500">
                      {amenity.value}
                    </Paragraphs.PropertyPage>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PropertyAmenitiesSection
